import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useHover } from 'usehooks-ts';
import Icon from '../icon/icon';
import { iconNames } from '../icon/types';
import Spinner, { SpinnerColors } from '../spinner/spinner';

/* eslint-disable-next-line */
export interface ButtonProps {
    id?: string;
    iconName?: iconNames;
    iconSize?: number;
    label?: string;
    className?: string;
    textClassName?: string;
    iconClassName?: string;
    size?: Size;
    title?: string;
    variant?: ButtonVariant;
    color?: ButtonBackground;
    rounded?: BeveledType;
    fluid?: boolean;
    position?: 'right' | 'left';
    disabled?: boolean;
    onClick?: (evt: React.MouseEvent) => void;
    loading?: boolean;
    href?: string;
    target?: string;
    type?: Type;
    dataTestId?: string;
    tooltip?: string;
}

type Size = 'md' | 'lg' | 'sm' | 'xs';

type ButtonBackground = 'primary' | 'secondary' | 'default' | 'variant';

type BeveledType = 'default' | 'rounded';

type ButtonVariant = 'outlined' | 'ghost' | 'default';

type Type = 'button' | 'submit' | 'reset' | undefined;

export function Button(props: ButtonProps) {
    const ref = useRef<HTMLButtonElement>(null);
    const isHover = useHover(ref);
    const buttonSizes = {
        xs: `text-xs py-1 ${props.label ? 'px-2' : 'px-1'}`,
        sm: `text-sm py-1 ${props.label ? 'px-3' : 'px-1.5'}`,
        md: `h-[38px] text-sm leading-4 py-2 ${props.label ? 'px-4' : 'px-2'}`,
        lg: `text-sm py-3 ${props.label ? 'px-4' : 'px-3.5'}`,
    };

    const buttonBackground = {
        primary: {
            default:
                'bg-primary-700 hover:enabled:bg-primary active:bg-primary-700 disabled:bg-primary-100 border border-primary active:border-primary-100 disabled:border-primary-100 text-white',
            ghost: 'bg-white hover:enabled:bg-primary-50 active:bg-primary-50 disabled:hover:bg-white border border-white active:border-primary-100 disabled:border-white text-primary disabled:text-primary-100',
            outlined:
                'bg-white hover:enabled:bg-primary disabled:hover:bg-white border border-primary active:border-primary-50 disabled:border-primary-100 text-primary active:text-white hover:enabled:text-white disabled:text-primary-100',
        },
        secondary: {
            default:
                'bg-secondary hover:enabled:bg-secondary-700 active:bg-secondary-700 disabled:bg-secondary-100 border border-secondary hover:enabled:border-secondary-700 active:border-secondary-100 disabled:border-secondary-100 text-white',
            ghost: 'bg-white hover:enabled:bg-secondary-50 active:bg-secondary-50 disabled:bg-white border border-white hover:enabled:border-secondary-50 active:border-secondary-100 text-secondary disabled:text-secondary-100 disabled:hover:border-white',
            outlined:
                'bg-white hover:enabled:bg-secondary active:bg-secondary disabled:bg-white border border-secondary active:border-secondary-100 disabled:border-secondary-100 text-secondary hover:enabled:text-white disabled:text-secondary-100',
        },
        default: {
            default:
                'bg-gray-700 hover:enabled:bg-gray-900 active:bg-gray-900 disabled:bg-gray-100 border border-gray-700 hover:enabled:border-gray-900 active:border-gray-100 disabled:border-gray-100 text-white',
            ghost: 'bg-white hover:enabled:bg-gray-50 active:bg-gray-50 disabled:bg-white border border-white hover:enabled:border-gray-50 active:border-gray-100 disabled:border-white text-gray-700 disabled:text-gray-100',
            outlined:
                'bg-white hover:enabled:bg-gray-50 border-gray-700 active:bg-gray-50 text-gray-700 disabled:bg-white border border-gray-700 active:border-gray-100 text-gray-700 disabled:border-gray-100 text-gray-100 active:text-gray-700 hover:enabled:text-gray-700 disabled:text-gray-100 text-gray-700',
        },
        variant: {
            default:
                'bg-variant-800 hover:enabled:bg-variant-500 active:bg-variant-900 disabled:bg-variant-100 border border-variant-800 hover:enabled:border-variant-100 active:border-variant-900 disabled:border-variant-100 text-white',
            ghost: 'bg-white hover:enabled:bg-variant-50 active:bg-variant-50 disabled:bg-white border-2 border-white hover:enabled:border-variant-50 active:border-variant-100 disabled:border-white text-variant-700 disabled:text-variant-100',
            outlined:
                'bg-white hover:enabled:bg-gray-50 active:bg-gray-50 disabled:bg-white border border-gray-400 active:border-gray-400 disabled:border-gray-200 text-gray-600 active:text-gray-600 hover:enabled:text-gray-600 disabled:text-gray-200',
        },
    };

    const iconColor = {
        primary: {
            default: 'text-white',
            ghost: 'text-primary group-disabled:text-primary-100',
            outlined:
                'text-primary group-hover:group-enabled:text-white group-active:text-white group-disabled:text-primary-100',
        },
        secondary: {
            default: 'text-white',
            ghost: 'text-secondary group-disabled:text-secondary-100',
            outlined:
                'text-secondary group-hover:group-enabled:text-white group-active:text-white group-disabled:text-secondary-100',
        },
        default: {
            default: 'text-white',
            ghost: 'text-gray-700 group-disabled:text-gray-100',
            outlined:
                'text-gray-700 group-hover:group-enabled:text-white group-active:text-white group-disabled:text-gray-100',
        },
        variant: {
            default: 'text-white',
            ghost: 'text-variant-700 group-disabled:text-variant-100',
            outlined:
                'text-gray-600 group-hover:group-enabled:text-gray-600 group-active:text-gray-600 group-disabled:text-gray-200',
        },
    };
    const iconColorSpinner = {
        primary: {
            default: 'default',
            ghost: 'primary',
            outlined: 'primary',
        },
        secondary: {
            default: 'default',
            ghost: 'secondary',
            outlined: 'secondary',
        },
        default: {
            default: 'default',
            ghost: 'gray',
            outlined: 'gray',
        },
        variant: {
            default: 'default',
            ghost: 'variant',
            outlined: 'gray',
        },
    };
    const beveled = {
        default: 'rounded',
        rounded: 'rounded-full',
    };
    const classes = twMerge(
        'flex transition font-poppins font-semibold group box-border justify-center items-center relative',
        buttonSizes[props.size || 'md'],
        buttonBackground[props.color || 'default'][props.variant || 'default'],
        beveled[props.rounded || 'default'],
        props.fluid && 'w-full',
        props.position === 'left' ? 'flex-row' : 'flex-row-reverse',
        props.className,
    );
    const iconClasses = twMerge(
        props.label && props.position === 'left' && 'mr-2',
        props.label && props.position === 'right' && 'ml-2',
        props.position === 'left' ? 'mr-2' : 'ml-2',
        iconColor[props.color || 'default'][props.variant || 'default'],
        props.iconClassName,
    );
    const iconSize = props.iconSize ? props.iconSize : 16;

    const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (props.onClick) return props.onClick(event);
        else if (props.href && props.target) return window.open(props.href, props.target);
        else if (props.href) return (window.location.href = props.href);
    };

    return (
        <button
            ref={ref}
            id={props.id}
            className={classes}
            disabled={props.disabled}
            title={props.title}
            data-test-id={props.dataTestId ? props.dataTestId : 'button'}
            data-testid={props.dataTestId ? props.dataTestId : 'button'}
            type={props.type}
            {...(props.onClick || props.href ? { onClick: onClickHandler } : {})}
        >
            {props.loading ? (
                <Spinner
                    size={props.size === 'xs' ? 12 : props.size === 'sm' ? 16 : props.size === 'md' ? 20 : 24}
                    className={iconColor[props.color || 'default'][props.variant || 'default']}
                    color={
                        (iconColorSpinner[props.color || 'default'][props.variant || 'default'] as SpinnerColors) ||
                        ('primary' as SpinnerColors)
                    }
                />
            ) : (
                <span className={`flex items-center ${props.position === 'left' ? 'flex-row' : 'flex-row-reverse'}`}>
                    {props.iconName && (
                        <Icon data-testid="icon" className={iconClasses} name={props.iconName} size={iconSize} />
                    )}
                    <p className={props.textClassName}>{props.label}</p>
                </span>
            )}
            {props.tooltip && isHover && ref.current ? (
                <span
                    style={{
                        top: ref.current.offsetHeight + 10,
                        zIndex: 9999,
                    }}
                    className={`absolute font-normal text-white text-[14px] leading-[14px] bg-slate-700 rounded p-1 z-10`}
                >
                    {props.tooltip}
                </span>
            ) : null}
        </button>
    );
}

Button.defaultProps = {
    size: 'md',
    variant: 'default',
    color: 'default',
    rounded: 'default',
    fluid: false,
    position: 'left',
    disabled: false,
    type: 'button',
};

export default Button;
